import { gql } from "@apollo/client";
import { isHourlyType } from "@asmbl/shared/compensation";
import { getNewPay } from "@asmbl/shared/employee";
import { getSalaryBand } from "src/models/Band";
import { CondensedTableNewSalaryBandCell2_participant as Participant } from "../../../../__generated__/graphql";
import { CondensedBandVisualization } from "../../../../components/CompSlider/CondensedBandVisualization";
import { NullCompSlider } from "../../../../components/CompSlider/NullCompSlider";
import { useCurrencies } from "../../../../components/CurrenciesContext";
import { isBandPointDefined } from "../../../../models/BandPoint";
import { ColumnComponent2 } from "../CondensedTable/CondensedTableInner";
import { ColumnIds, ColumnIdsToHeaders } from "../Contexts/ColumnOrderContext";
import { BAND_COL_WIDTH } from "./dimensions";

type Props = {
  row: { original: Participant };
};
export function CondensedTableNewSalaryBandCell2({
  row: { original: employee },
}: Props): JSX.Element {
  const { currencies, defaultCurrency } = useCurrencies();
  const promoItemBand = employee.compRecommendation?.latestSubmittedItems.find(
    (item) => item.adjustedCashBands !== null
  );

  const adjustedSalaryBand =
    getSalaryBand(promoItemBand?.adjustedCashBands) ??
    getSalaryBand(employee.subject.adjustedCashBands);

  const newSalary = getNewPay(
    {
      activeCashCompensation: employee.subject.activeCashCompensation,
      compRecommendation: employee.compRecommendation,
    },
    currencies,
    defaultCurrency
  );
  const newPosition = getNewPosition(employee);

  return (
    <div style={{ display: "flex", justifyContent: "center" }}>
      {newSalary &&
      adjustedSalaryBand &&
      adjustedSalaryBand.bandPoints.some(isBandPointDefined) ? (
        <CondensedBandVisualization
          bandPoints={adjustedSalaryBand.bandPoints}
          value={
            isHourlyType(newPosition?.type)
              ? newSalary.hourlyCashEquivalent
              : newSalary.annualCashEquivalent
          }
          position={newPosition}
          outOfRangeStyle="circle"
          showHourly={isHourlyType(newPosition?.type)}
        />
      ) : (
        <NullCompSlider variant="condensed" />
      )}
    </div>
  );
}

function getNewPosition(employee: Participant) {
  const recItems = employee.compRecommendation?.latestSubmittedItems ?? [];

  const newPosition = recItems.find(
    (item) => item.recommendedPosition != null
  )?.recommendedPosition;

  return newPosition ?? employee.subject.activeEmployment?.position ?? null;
}

const positionFragment = gql`
  fragment CondensedTableNewSalaryBandCell2_position on Position {
    id
    name
    type
    level
    ladder {
      id
      name
      department {
        id
        name
      }
    }
  }
`;

CondensedTableNewSalaryBandCell2.fragments = {
  participant: gql`
    ${positionFragment}
    fragment CondensedTableNewSalaryBandCell2_participant on CompCycleParticipant {
      subject {
        adjustedCashBands(currencyCode: $currencyCode) {
          id
          name
          bandPoints {
            name
            value {
              ... on CashValue {
                annualRate
                hourlyRate
                currencyCode
              }
            }
          }
        }
        activeCashCompensation(currencyCode: $currencyCode) {
          employeeId
          type
          activeAt
          annualCashEquivalent
          hourlyCashEquivalent
          unit
        }
        activeEmployment {
          id
          position {
            ...CondensedTableNewSalaryBandCell2_position
          }
        }
      }
      compRecommendation(skipEligibility: $skipEligibility) {
        subjectId
        compCycleId
        latestSubmittedPayIncrease(currencyCode: $currencyCode) {
          annualCashEquivalent
          hourlyCashEquivalent
          unitType
        }
        latestSubmittedItems {
          id
          recommendationType
          recommendedCashValue(currencyCode: $currencyCode)
          recommendedPosition {
            ...CondensedTableNewSalaryBandCell2_position
          }
          adjustedCashBands(currencyCode: $currencyCode) {
            id
            name
            bandPoints {
              name
              value {
                ... on CashValue {
                  annualRate
                  hourlyRate
                  currencyCode
                }
              }
            }
          }
        }
      }
    }
  `,
};

CondensedTableNewSalaryBandCell2.Header = ColumnIdsToHeaders.get(
  ColumnIds.NEW_BAND
);
CondensedTableNewSalaryBandCell2.id = ColumnIds.NEW_BAND;

const column: ColumnComponent2["column"] = {
  Cell: CondensedTableNewSalaryBandCell2,
  Header: CondensedTableNewSalaryBandCell2.Header,
  id: CondensedTableNewSalaryBandCell2.id,
  width: BAND_COL_WIDTH,
  defaultCanSort: false,
};
CondensedTableNewSalaryBandCell2.column = column;
